import { Controller } from "@hotwired/stimulus";
import consumer from "../channels/consumer";

const RETRY_BASE = 1000;
const RETRY_MAX = 30000;

// Connects to data-controller="map-follow", on the Mapas page while the campaign has no
// map and on the table view while no scene is on the table: it opens the scene the master
// activates, in the table view on the table. The players' stream says only
// `reload`, so that and each (re)connection ask the list, which also catches
// an activation missed while away. A master's stream says it with diffs, so
// any message on it asks the list. A refused or revoked subscription
// swaps the waiting copy for the board's "no access" label, and a later
// reconnection does not bring it back. The table link follows its own stream
// and address instead (channel and tableUrl), which name no scene.
export default class extends Controller<HTMLElement> {
	static targets = ["message"];
	static values = { campaignId: String, scenesUrl: String, table: Boolean, master: Boolean, rejected: String, channel: Object, tableUrl: String };
	declare readonly messageTarget: HTMLElement;
	declare readonly campaignIdValue: string;
	declare readonly scenesUrlValue: string;
	declare readonly tableValue: boolean;
	declare readonly masterValue: boolean;
	declare readonly rejectedValue: string;
	declare readonly channelValue: object;
	declare readonly hasChannelValue: boolean;
	declare readonly tableUrlValue: string;
	declare readonly hasTableUrlValue: boolean;
	subscription?: { unsubscribe(): void };
	denied = false;
	halted = false;
	opening = false;
	generation = 0;
	retryDelay = RETRY_BASE;
	retryTimer?: ReturnType<typeof setTimeout>;

	connect() {
		this.halted = false;
		document.addEventListener("turbo:fetch-request-error", this.visitFailed);
		document.addEventListener("visibilitychange", this.recheck);
		window.addEventListener("online", this.recheck);
		const channel = { channel: "MapChannel", campaign_id: this.campaignIdValue, ...(this.tableValue ? { view: "players" } : {}) };
		this.subscription = consumer.subscriptions.create(this.hasChannelValue ? this.channelValue : channel, {
			connected: () => {
				if (this.denied) return;
				this.element.dataset.liveStatus = "live";
				this.check();
			},
			disconnected: () => {
				if (!this.denied) this.element.dataset.liveStatus = "offline";
			},
			rejected: () => this.deny(),
			received: (message) => {
				if (message?.type === "revoked") return this.deny();
				if (message?.type !== "reload" && !this.masterValue) return;
				if (this.hasTableUrlValue) this.visit(this.tableUrlValue);
				else this.check();
			},
		});
	}

	disconnect() {
		document.removeEventListener("turbo:fetch-request-error", this.visitFailed);
		document.removeEventListener("visibilitychange", this.recheck);
		window.removeEventListener("online", this.recheck);
		this.stop();
		this.subscription?.unsubscribe();
	}

	// A late answer to a check in flight neither navigates nor retries.
	stop() {
		this.generation++;
		clearTimeout(this.retryTimer);
	}

	deny() {
		this.stop();
		this.denied = true;
		this.element.dataset.liveStatus = "rejected";
		this.messageTarget.textContent = this.rejectedValue;
	}

	visitFailed = () => {
		this.opening = false;
	};

	recheck = () => {
		if (document.visibilityState !== "hidden") this.check();
	};

	retry() {
		if (this.opening) return;
		clearTimeout(this.retryTimer);
		this.retryTimer = setTimeout(() => this.check(), this.retryDelay);
		this.retryDelay = Math.min(this.retryDelay * 2, RETRY_MAX);
	}

	async check() {
		if (this.denied || this.halted) return;
		clearTimeout(this.retryTimer);
		const generation = this.generation;
		const url = this.hasTableUrlValue ? this.tableUrlValue : this.scenesUrlValue;
		try {
			const response = await fetch(`${url}.json`, { headers: { Accept: "application/json" }, credentials: "same-origin", cache: "no-store" });
			if (generation !== this.generation) return;
			if (response.status === 401 || response.status === 403) return this.deny();
			if (response.status === 404) {
				this.halted = true;
				return this.stop();
			}
			if (!response.ok) return this.retry();
			const body = await response.json();
			if (generation !== this.generation) return;
			this.retryDelay = RETRY_BASE;
			if (this.hasTableUrlValue) {
				if (body.scene) this.visit(this.tableUrlValue);
				return;
			}
			const active = body.scenes?.find((scene) => scene.active);
			if (active) this.open(active.id);
		} catch {
			if (generation === this.generation) this.retry();
		}
	}

	open(id: string) {
		this.visit(`${this.scenesUrlValue}/${id}${this.tableValue ? "/mesa" : ""}`);
	}

	visit(url: string) {
		if (this.opening) return;
		this.opening = true;
		Turbo.visit(url);
	}
}
